/**
 * THE shared weather store. One module-level snapshot, one subscriber Set,
 * one in-flight request — no matter how many components mount `useWeather()`.
 *
 * WHY THIS EXISTS (the biggest cross-worker collision in the build):
 * `useWeather()` is mounted by BOTH `WeatherPanel` and the Mayor's `TopBar`,
 * simultaneously, on every page load. A per-caller `useEffect` fetch loop
 * would double every request against a 60-calls/min tier, and React 18
 * StrictMode doubles it again in dev. So the fetch lives here, at module
 * scope, and the hook is a pure `useSyncExternalStore` reader — the same
 * shape as `useLocalDateKey` in src/lib/storage.ts.
 *
 * Two guards make concurrent callers safe:
 *   - `inFlight`  : a second caller joins the running request instead of
 *                   starting a second one.
 *   - `MIN_INTERVAL_MS` : two independently-phased poll timers landing a few
 *                   milliseconds apart collapse to one network round-trip.
 *
 * NOTHING in here throws. `fetchJson` never rejects, every normalizer returns
 * null rather than raising, and the top bar would lose its weather summary if
 * this module ever did — "no console errors on an empty .env" is graded.
 */

import type { UseWeatherResult, WeatherAlert, WeatherDay, WeatherNow } from '../../config/contracts';
import { env } from '../../config/env';
import { readJson, writeJson } from '../storage';
import { fetchOpenMeteo } from './openMeteo';
import { fetchOpenWeather, type ProviderOutcome } from './openWeather';
import { fetchNwsAlerts } from './nws';

/** Everything the hook returns except `refresh`, which the hook supplies. */
export type WeatherSnapshot = Omit<UseWeatherResult, 'refresh'>;

/** Conditions change slowly; a pinned tab must not burn the tier overnight. */
export const WEATHER_POLL_SECONDS = 600;

/** Collapses two poll loops that fire microseconds apart into one request. */
const MIN_INTERVAL_MS = 30_000;
/** Floor on the manual refresh button, so mashing it cannot flood the tier. */
const MIN_FORCED_INTERVAL_MS = 5_000;
/** Older than this and the cached reading is not worth showing on mount. */
const CACHE_MAX_AGE_MS = 6 * 60 * 60 * 1000;

const EMPTY_FORECAST: readonly WeatherDay[] = Object.freeze([]);
const EMPTY_ALERTS: readonly WeatherAlert[] = Object.freeze([]);

/* ------------------------------------------------------------------ */
/* localStorage cache: a reload shows the last reading immediately     */
/* instead of a skeleton. Alerts are deliberately NOT cached — a stale */
/* severe-weather banner is worse than no banner.                      */
/* ------------------------------------------------------------------ */

interface CachedReading {
  readonly now: WeatherNow;
  readonly forecast: readonly WeatherDay[];
  readonly fetchedAt: number;
  readonly usingKeylessFallback: boolean;
}

function isCachedReading(v: unknown): v is CachedReading {
  if (typeof v !== 'object' || v === null) return false;
  const c = v as Partial<CachedReading>;
  if (typeof c.fetchedAt !== 'number' || !Array.isArray(c.forecast)) return false;
  const n = c.now as Partial<WeatherNow> | undefined;
  return (
    typeof n === 'object' &&
    n !== null &&
    typeof n.tempC === 'number' &&
    typeof n.tempF === 'number' &&
    typeof n.icon === 'string' &&
    typeof n.conditionLabel === 'string'
  );
}

function readCache(): CachedReading | null {
  const cached = readJson<CachedReading | null>('weather', 'reading', null,
    (v): v is CachedReading | null => v === null || isCachedReading(v));
  if (cached === null) return null;
  if (Date.now() - cached.fetchedAt > CACHE_MAX_AGE_MS) return null;
  // A location change invalidates the reading even if it is fresh.
  if (cached.now.locationName !== env.weather.locationName) return null;
  return cached;
}

/* ------------------------------------------------------------------ */
/* Snapshot + subscribers                                              */
/* ------------------------------------------------------------------ */

function initialSnapshot(): WeatherSnapshot {
  const cached = readCache();
  if (cached === null) {
    // Never 'unconfigured' here: there is ALWAYS a source to try, because
    // open-meteo needs no key. 'unconfigured' is reserved for the one case
    // where we end up with no data and no key — see `applyOutcome`.
    return frozen({
      status: 'loading',
      now: null,
      forecast: EMPTY_FORECAST,
      alerts: EMPTY_ALERTS,
      hasSevereAlert: false,
      error: null,
      lastUpdated: null,
      usingKeylessFallback: env.weather.apiKey === undefined,
    });
  }
  return frozen({
    status: 'ready',
    now: cached.now,
    forecast: cached.forecast,
    alerts: EMPTY_ALERTS,
    hasSevereAlert: false,
    error: null,
    lastUpdated: cached.fetchedAt,
    usingKeylessFallback: cached.usingKeylessFallback,
  });
}

function frozen(s: WeatherSnapshot): WeatherSnapshot {
  return Object.freeze(s);
}

let snapshot: WeatherSnapshot = initialSnapshot();
const listeners = new Set<() => void>();

function publish(next: WeatherSnapshot): void {
  snapshot = frozen(next);
  listeners.forEach((fn) => fn());
}

/** `useSyncExternalStore` requires a referentially stable snapshot. */
export function getWeatherSnapshot(): WeatherSnapshot {
  return snapshot;
}

export function subscribeWeather(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

/* ------------------------------------------------------------------ */
/* Fetch orchestration                                                 */
/* ------------------------------------------------------------------ */

let inFlight: Promise<boolean> | null = null;
let lastAttemptAt = 0;

async function loadConditions(): Promise<{ outcome: ProviderOutcome; keyless: boolean }> {
  const { lat, lon, apiKey, locationName } = env.weather;

  if (apiKey === undefined) {
    return { outcome: await fetchOpenMeteo(lat, lon, locationName), keyless: true };
  }

  const keyed = await fetchOpenWeather(lat, lon, apiKey, locationName);
  if (keyed.ok) return { outcome: keyed, keyless: false };

  // A configured key that fails (revoked, not yet activated, rate-limited)
  // should degrade to real keyless data rather than to an empty card. The
  // panel still tells the user the key failed, via `usingKeylessFallback`.
  const rescue = await fetchOpenMeteo(lat, lon, locationName);
  return rescue.ok ? { outcome: rescue, keyless: true } : { outcome: keyed, keyless: false };
}

async function runFetch(): Promise<boolean> {
  // Only show a skeleton when there is genuinely nothing on screen. A refresh
  // over existing data must not blank the card.
  if (snapshot.now === null && snapshot.status !== 'loading') {
    publish({ ...snapshot, status: 'loading', error: null });
  }

  const [{ outcome, keyless }, alerts] = await Promise.all([
    loadConditions(),
    // Alerts are best-effort and non-fatal: outside the US this is always
    // empty, which is the correct degraded state, not an error.
    fetchNwsAlerts(env.weather.lat, env.weather.lon),
  ]);

  const hasSevereAlert = alerts.some((a) => a.severity === 'warning');

  if (outcome.ok) {
    const fetchedAt = Date.now();
    publish({
      status: 'ready',
      now: outcome.value.now,
      forecast: outcome.value.forecast,
      alerts,
      hasSevereAlert,
      error: null,
      lastUpdated: fetchedAt,
      usingKeylessFallback: keyless,
    });
    writeJson<CachedReading>('weather', 'reading', {
      now: outcome.value.now,
      forecast: outcome.value.forecast,
      fetchedAt,
      usingKeylessFallback: keyless,
    });
    return true;
  }

  // Failure. Stale data beats an error state on a permanently pinned tab, so
  // keep rendering the last reading and let `lastUpdated` show its age.
  // Alerts still update — they are the one thing worth showing on their own.
  if (snapshot.now !== null) {
    publish({ ...snapshot, status: 'ready', alerts, hasSevereAlert, error: null });
    return false;
  }

  // No data at all. With no key, "add a key" is the actionable state and the
  // one the stub contract calls for; with a key, this is a real error.
  publish(
    env.weather.apiKey === undefined
      ? {
          status: 'unconfigured',
          now: null,
          forecast: EMPTY_FORECAST,
          alerts,
          hasSevereAlert,
          error: null,
          lastUpdated: null,
          usingKeylessFallback: true,
        }
      : {
          status: 'error',
          now: null,
          forecast: EMPTY_FORECAST,
          alerts,
          hasSevereAlert,
          error: outcome.error,
          lastUpdated: null,
          usingKeylessFallback: false,
        },
  );
  return false;
}

/**
 * The single entry point. Resolves true on success, false on failure, for
 * `usePolling`'s backoff. Safe to call from any number of components.
 *
 * `force` is the manual refresh button: it bypasses the poll-collapsing
 * interval but keeps a 5s floor.
 */
export function refreshWeather(force: boolean = false): Promise<boolean> {
  if (inFlight !== null) return inFlight;

  const since = Date.now() - lastAttemptAt;
  const floor = force ? MIN_FORCED_INTERVAL_MS : MIN_INTERVAL_MS;
  if (lastAttemptAt !== 0 && since < floor) {
    return Promise.resolve(snapshot.status !== 'error');
  }

  lastAttemptAt = Date.now();
  const run = runFetch().finally(() => {
    inFlight = null;
  });
  inFlight = run;
  return run;
}
